import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Topic } from '../../types/Dashboard';
import { logout } from '../auth/authSlice';
import { examUpdated } from '../exams/examsSlice';
import { setActiveFilters } from '../activeTopics/activeTopicsSlice';

type TopicsStatus = 'idle' | 'loading' | 'succeeded' | 'failed' | 'stale';

export interface TodayTopicsState {
  topics: Topic[];
  status: TopicsStatus;
  error: string | null;
  lastFetchedDate: string | null;
  lastFetchedAt: number | null;
}

const initialState: TodayTopicsState = {
  topics: [],
  status: 'idle',
  error: null,
  lastFetchedDate: null,
  lastFetchedAt: null,
};

// Cache expiration time: 30 minutes
const CACHE_EXPIRATION_MS = 30 * 60 * 1000;

export const todayTopicsSlice = createSlice({
  name: 'todayTopics',
  initialState,
  reducers: {
    topicsLoading: (state) => {
      state.status = 'loading';
      state.error = null;
    },
    topicsReceived: (
      state,
      action: PayloadAction<{ topics: Topic[]; date: string }>
    ) => {
      // De-duplicate topics based on their ID
      const uniqueTopics = action.payload.topics.reduce(
        (acc: Topic[], current: Topic) => {
          const isDuplicate = acc.some((topic) => topic.id === current.id);
          if (!isDuplicate) {
            acc.push(current);
          }
          return acc;
        },
        []
      );

      state.topics = uniqueTopics;
      state.status = 'succeeded';
      state.error = null;
      state.lastFetchedDate = action.payload.date;
      state.lastFetchedAt = Date.now();
    },
    topicsFailed: (state, action: PayloadAction<string>) => {
      state.status = 'failed';
      state.error = action.payload;
    },
    // Mark topics as stale without clearing them
    markTopicsStale: (state) => {
      state.status = 'stale';
    },
    // Force invalidate - clears topics and forces refetch
    invalidateTopics: (state) => {
      state.topics = [];
      state.status = 'idle';
      state.lastFetchedDate = null;
      state.lastFetchedAt = null;
      state.error = null;
    },
    clearTopics: (state) => {
      state.topics = [];
      state.status = 'idle';
      state.error = null;
      state.lastFetchedDate = null;
      state.lastFetchedAt = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(logout, (state) => {
        state.topics = [];
        state.status = 'idle';
        state.error = null;
        state.lastFetchedDate = null;
        state.lastFetchedAt = null;
      })
      // Invalidate topics when topic filters change
      .addCase(setActiveFilters, (state) => {
        state.status = 'stale';
      })
      // Invalidate topics when exam settings change
      .addCase(examUpdated, (state, action) => {
        if (action.payload.primary === true) {
          state.status = 'stale';
        }
      });
  },
});

// Selectors
export const selectTodayTopics = (state: { todayTopics: TodayTopicsState }) =>
  state.todayTopics.topics || [];

export const selectTodayTopicsStatus = (state: {
  todayTopics: TodayTopicsState;
}) => state.todayTopics.status || 'idle';

export const selectTodayTopicsError = (state: {
  todayTopics: TodayTopicsState;
}) => state.todayTopics.error;

// Smart cache selector
export const selectShouldFetchTopics =
  (date: string) => (state: { todayTopics: TodayTopicsState }) => {
    const { lastFetchedDate, lastFetchedAt, status } = state.todayTopics;

    // Don't fetch if already loading
    if (status === 'loading') return false;

    // Always fetch if marked as stale
    if (status === 'stale') return true;

    // Fetch if different date
    if (lastFetchedDate !== date) return true;

    // Fetch if cache expired
    if (!lastFetchedAt || Date.now() - lastFetchedAt > CACHE_EXPIRATION_MS)
      return true;

    // Don't fetch if we have recent data for the same date
    return false;
  };

export const {
  topicsLoading,
  topicsReceived,
  topicsFailed,
  markTopicsStale,
  invalidateTopics,
  clearTopics,
} = todayTopicsSlice.actions;

export default todayTopicsSlice.reducer;
